import type { AutomationRun } from '@servicetim/shared';
import { useAutomationRuns } from '@servicetim/ui/api';
import { Badge, type ColumnDef, DataTable, pagedTableProps } from '@servicetim/ui/components';
import { Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { formatDateTime } from '../tenant/common/format';

const STATUS_VARIANT: Record<
  AutomationRun['status'],
  'success' | 'destructive' | 'secondary' | 'warning'
> = {
  succeeded: 'success',
  failed: 'destructive',
  skipped_conditions: 'secondary',
  skipped_depth: 'secondary',
  skipped_suppressed: 'secondary',
  dry_run: 'warning',
};

/** Run history of one rule (newest first): status, duration, applied actions, ticket link. */
export function RuleRunsPanel({ ruleId }: { ruleId: string }) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const runs = useAutomationRuns(ruleId);

  const columns = useMemo<ColumnDef<AutomationRun, unknown>[]>(
    () => [
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <Badge variant={STATUS_VARIANT[row.original.status]}>{row.original.status}</Badge>
        ),
      },
      {
        accessorKey: 'startedAt',
        header: 'Started',
        cell: ({ row }) => (
          <span className="text-fg-muted">{formatDateTime(row.original.startedAt)}</span>
        ),
      },
      {
        accessorKey: 'durationMs',
        header: 'Duration',
        cell: ({ row }) =>
          row.original.durationMs === null ? '—' : <span>{row.original.durationMs} ms</span>,
      },
      {
        id: 'ticket',
        header: 'Ticket',
        enableSorting: false,
        cell: ({ row }) =>
          row.original.ticketId ? (
            <Link
              to="/tickets/$idOrKey"
              params={{ idOrKey: row.original.ticketId }}
              className="font-mono text-accent text-xs underline-offset-4 hover:underline"
            >
              {row.original.ticketId.slice(0, 8)}…
            </Link>
          ) : (
            <span className="text-fg-muted">—</span>
          ),
      },
      {
        id: 'actionsApplied',
        header: 'Actions applied',
        enableSorting: false,
        cell: ({ row }) => row.original.result.actionsApplied.length,
      },
    ],
    [],
  );

  return (
    <div className="space-y-3">
      <DataTable
        columns={columns}
        {...pagedTableProps(runs)}
        getRowId={(r) => r.id}
        caption="Automation runs"
        onRowClick={(r) => setExpanded((current) => (current === r.id ? null : r.id))}
        emptyTitle="No runs yet"
        emptyDescription="Runs appear here once the trigger fires (or a dry run is used)."
      />
      {expanded
        ? (() => {
            const run = runs.items.find((r) => r.id === expanded);
            return run ? (
              <pre className="max-h-64 overflow-auto rounded-md border border-border bg-surface-sunken p-3 text-xs">
                {JSON.stringify(run.result, null, 2)}
              </pre>
            ) : null;
          })()
        : null}
    </div>
  );
}
